import Vue from 'vue'
import store from '@/store/index'

const PING_INTERVAL = 1000

interface Cammer {
  user_configs: any[]; // eslint-disable-line @typescript-eslint/no-explicit-any, camelcase
}

interface LatencyData {
  value: number;
  date: string;
}

interface Socket {
  on<T>(event: string, callback: (data: T) => void): void;
  emit<T>(event: string, data: T): void;
  off<T>(event: string, callback: (data: T) => void): void;
}

export default class ConnectionQuality {
  private data: LatencyData[];
  private socket: Socket;
  private pingInterval: ReturnType<typeof setInterval> | null;
  private GENERATE_DATA = false;
  private SHOW_ICONS = false;
  private SHOW_CHARTS = false;

  private config: any; // eslint-disable-line @typescript-eslint/no-explicit-any
  private defaultConfig: any = { // eslint-disable-line @typescript-eslint/no-explicit-any
    cammer_app_generate_data: false,
    widget_app_generate_data: false,
    show_icons: {
      widget: false,
      cammer: false
    },
    show_charts: {
      widget: false,
      cammer: false
    }
  };

  constructor (socket: Socket) {
    this.data = []
    this.socket = socket
    this.pingInterval = null

    this.GENERATE_DATA = false
    this.SHOW_ICONS = false
    this.SHOW_CHARTS = false
  }

  private getLocaleTime (): string {
    return new Date().toLocaleTimeString()
  }

  public getAverageLatency (): number {
    if (this.data.length === 0) return 0
    const total = this.data.reduce((acc, latency) => acc + latency.value, 0)
    return total / this.data.length
  }

  private updateWidgetLatency (data: any): void { // eslint-disable-line @typescript-eslint/no-explicit-any
    Vue.prototype.$socketEvents.sendSignalLatency(data)
  }

  private onHealthPong (startTime: number): void {
    const latency = Date.now() - startTime

    if (this.GENERATE_DATA || this.SHOW_ICONS || this.SHOW_CHARTS) {
      this.updateWidgetLatency({
        value: latency,
        date: this.getLocaleTime()
      })
    }
  }

  public initialize (): void {
    if (this.pingInterval) return

    this.GENERATE_DATA = (this.config?.widget_app_generate_data === true)
    this.SHOW_ICONS = (this.config?.show_icons?.widget === true)
    this.SHOW_CHARTS = (this.config?.show_charts?.widget === true)

    if (!this.GENERATE_DATA && !this.SHOW_ICONS && !this.SHOW_CHARTS) return

    this.socket.on('health_pong', this.onHealthPong.bind(this))

    this.pingInterval = setInterval(() => {
      const startTime = Date.now()
      this.socket.emit('health_ping', startTime)
    }, PING_INTERVAL)
  }

  public stop (): void {
    if (this.pingInterval) clearInterval(this.pingInterval)
    this.pingInterval = null
  }

  public isActiveForCammer (cammer: Cammer): boolean {
    const storage: any = store.state.storage // eslint-disable-line @typescript-eslint/no-explicit-any
    const session: any = storage?.room?.session ?? null // eslint-disable-line @typescript-eslint/no-explicit-any

    if (!session) return false

    const userConfig = cammer?.user_configs.find((item: any) => item.type === 22 && item.value === 'true') ?? null // eslint-disable-line @typescript-eslint/no-explicit-any
    const isActive = userConfig && session.id

    this.config = userConfig?.data ?? this.defaultConfig

    return isActive
  }

  public terminate (): void {
    this.stop()
    this.socket.off('health_pong', this.onHealthPong.bind(this))
  }
}
